import { useContext } from "react";

import { useQueryClient } from "react-query";
import invariant from "ts-invariant";

import { createPatientCareEvent, getPatientCareEventsQueryKey } from "api/hooks/useGetPatientCareEventsQuery";
import { getPTCareEventsQueryKey } from "api/hooks/useGetPTCareEventsQuery";
import type { CareEvent } from "api/models/CareEvent";
import { useProfileContext } from "contexts/ProfileContext";
import { newNoteSafe } from "routes/patients/utils/notesHelper";
import { CurrentPatientContext } from "utils/contexts";

export const useNoteCreation = () => {
  const { patient, updateIsCreatable, setActiveCareEventNote } = useContext(CurrentPatientContext);
  const { profile } = useProfileContext();
  const queryClient = useQueryClient();
  invariant(profile);

  const createNote = async (type: string): Promise<{ success: boolean; error?: Error }> => {
    try {
      const result = await newNoteSafe(patient.id, type, profile.id);
      if (result) {
        const newNoteData = result.data as CareEvent;
        const key = getPatientCareEventsQueryKey({ patientId: patient.id, signed: false });
        queryClient.cancelQueries(key);
        queryClient.setQueryData(key, createPatientCareEvent(newNoteData));
        setActiveCareEventNote(newNoteData.id);
        queryClient.invalidateQueries(key);
        queryClient.invalidateQueries(getPTCareEventsQueryKey({ userId: profile.id }));
        updateIsCreatable();
        return { success: true };
      }
      return { success: false };
    } catch (error) {
      return { success: false, error: error as Error };
    }
  };

  return {
    createNote,
  };
};
